import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, Users, Calendar, TrendingUp, AlertTriangle, CheckCircle,
  Clock, ArrowRight, Plus, RefreshCw, BarChart3, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';

const BDDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.getBDDashboard();
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to load BD dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to BD Desk');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadge = (risk) => {
    switch (risk) {
      case 'High':
        return <Badge className="bg-red-100 text-red-700">🔴 High</Badge>;
      case 'Medium':
        return <Badge className="bg-amber-100 text-amber-700">🟡 Medium</Badge>;
      default:
        return <Badge className="bg-emerald-100 text-emerald-700">🟢 Low</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  const widgets = dashboard?.widgets || {};
  const recentMeetings = dashboard?.recent_meetings || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Building2 size={28} />
                BD Desk
              </h1>
              <p className="text-purple-200 text-sm mt-1">Business Development Command Center</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/bd/builders/new')}
                className="bg-white/20 hover:bg-white/30 text-white"
              >
                <Plus size={16} className="mr-1" />
                Add Builder
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Summary Widgets */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card 
            className="p-4 bg-white cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/bd/meetings')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{widgets.meetings_mtd || 0}</p>
                <p className="text-xs text-slate-500">Meetings MTD</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 bg-white cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/bd/pipeline')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <TrendingUp size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{widgets.projects_onboarded_mtd || 0}</p>
                <p className="text-xs text-slate-500">Onboarded MTD</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 bg-white cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/bd/builders')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{widgets.active_builders || 0}</p>
                <p className="text-xs text-slate-500">Active Builders</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 bg-white cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/bd/pipeline')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Building2 size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{widgets.projects_in_discussion || 0}</p>
                <p className="text-xs text-slate-500">In Discussion</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 bg-white cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/bd/meetings?action_status=Pending')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{widgets.meetings_pending_action || 0}</p>
                <p className="text-xs text-slate-500">Pending Actions</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 bg-white cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/bd/meetings?risk_level=High')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{widgets.high_risk_builders || 0}</p>
                <p className="text-xs text-slate-500">High Risk</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card 
            className="p-4 bg-gradient-to-br from-purple-500 to-indigo-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/bd/builders')}
            data-testid="bd-quick-action-builders"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Builder Directory</h3>
                <p className="text-sm text-purple-200">Manage all builders</p>
              </div>
              <ArrowRight size={24} />
            </div>
          </Card>

          <Card 
            className="p-4 bg-gradient-to-br from-emerald-500 to-teal-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/bd/pipeline')}
            data-testid="bd-quick-action-pipeline"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Onboarding Pipeline</h3>
                <p className="text-sm text-emerald-200">Track project stages</p>
              </div>
              <ArrowRight size={24} />
            </div>
          </Card>

          <Card 
            className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/bd/revenue')}
            data-testid="bd-quick-action-revenue"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Revenue Pipeline</h3>
                <p className="text-sm text-green-200">Brokerage Collection</p>
              </div>
              <IndianRupee size={24} />
            </div>
          </Card>

          <Card 
            className="p-4 bg-gradient-to-br from-amber-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/bd/analytics')}
            data-testid="bd-quick-action-analytics"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Analytics</h3>
                <p className="text-sm text-amber-200">Revenue & Pipeline Insights</p>
              </div>
              <BarChart3 size={24} />
            </div>
          </Card>

          <Card 
            className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/bd/meetings/new')}
            data-testid="bd-quick-action-meeting"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Log Meeting</h3>
                <p className="text-sm text-blue-200">Record interactions</p>
              </div>
              <Plus size={24} />
            </div>
          </Card>
        </div>

        {/* Recent Meetings Table */}
        <Card className="bg-white rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Recent Meetings</h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/bd/meetings')}
            >
              View All
            </Button>
          </div>
          
          {recentMeetings.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Calendar size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No meetings recorded yet</p>
              <Button 
                className="mt-4"
                onClick={() => navigate('/bd/meetings/new')}
              >
                Log First Meeting
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Builder</th>
                    <th className="px-4 py-3 text-left">Project</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Risk</th>
                    <th className="px-4 py-3 text-left">Next Action</th>
                    <th className="px-4 py-3 text-left">Due</th>
                    <th className="px-4 py-3 text-left">Owner</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentMeetings.map((meeting) => (
                    <tr 
                      key={meeting.meeting_id} 
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/bd/meetings/${meeting.meeting_id}`)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{meeting.builder_name}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {meeting.project_name || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {meeting.meeting_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {getRiskBadge(meeting.collection_risk_level)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-700 max-w-[200px] truncate">
                          {meeting.next_action_description}
                        </p>
                        {meeting.next_action_status === 'Completed' && (
                          <CheckCircle size={14} className="text-emerald-500 inline ml-1" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDate(meeting.next_action_deadline)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {meeting.next_action_owner_name || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default BDDashboard;
